import { vendorAPI } from "@api/vendorApi";
import InfiniteScrollObserver from "@component/InfiniteScrollObserver";
import useDebounce from "@hooks/useDebounce";
import type { VendorListItem } from "@page/VendorInfoPage/interface";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Select } from "antd";

interface VendorSelectProps {
  value?: number;
  onChange?: (vendorId: number) => void;
  onVendorChange?: (vendor: VendorListItem) => void;
  disabled?: boolean;
  placeholder?: string;
  showSearch?: boolean;
}

const PAGE_SIZE = 20;

const VendorSelect = ({ value, onChange, onVendorChange, disabled, placeholder, showSearch }: VendorSelectProps) => {
  const [results, setResults] = useState<VendorListItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const nameRef = useRef("");
  const offsetRef = useRef(0);

  const fetchList = useCallback(async (name: string, offset: number, append: boolean) => {
    const response = await vendorAPI.fetchVendorList({
      limit: PAGE_SIZE,
      offset,
      name: name || undefined,
    });
    setHasMore(response.next !== null);
    if (append) {
      setResults((prev) => [...prev, ...response.results]);
    } else {
      setResults(response.results);
    }
  }, []);

  useEffect(() => {
    fetchList("", 0, false);
  }, [fetchList]);

  const handleSearch = useDebounce((name: string) => {
    nameRef.current = name;
    offsetRef.current = 0;
    fetchList(name, 0, false);
  }, 300);

  const handleLoadMore = useCallback(() => {
    offsetRef.current += PAGE_SIZE;
    fetchList(nameRef.current, offsetRef.current, true);
  }, [fetchList]);

  const handleChange = (selectedId: number) => {
    onChange?.(selectedId);
    const vendor = results.find((v) => v.id === selectedId);
    if (vendor) onVendorChange?.(vendor);
  };

  const options = [
    ...results.map((vendor) => ({ label: vendor.name, value: vendor.id, disabled: false })),
    ...(hasMore
      ? [{ label: <InfiniteScrollObserver callback={handleLoadMore} />, value: "__loading__", disabled: true }]
      : []),
  ];

  return (
    <Select
      showSearch={showSearch}
      filterOption={false}
      value={value}
      onChange={handleChange}
      onSearch={handleSearch}
      disabled={disabled}
      placeholder={placeholder || "請選擇廠商"}
      options={options}
    />
  );
};

export default VendorSelect;
